<template>
  <div class="fill-height">
    <GridChartCard
      :title="title"
      @draw="createChart"
      :hasData="redrawFlag && data.length > 0"
      v-bind="{tooltip}"
      :canvasContainerHeight="canvasContainerHeight"
    >
      <template #actions>
        <slot name="actions"/>
      </template>
    </GridChartCard>
  </div>
</template>

<script>
import { Chart } from 'chart.js'
import { numberWithCommas, formatMoney } from '@/utils/NumericMutations.js'
import { getChartColor } from '@/components/dashboard/ChartColors'
import { mapGetters } from 'vuex'
export default {
  name: 'LineChartCard',

  components: {
    GridChartCard: () => import('@/components/dashboard/GridChartCard.vue')
  },

  props: {
    data: {
      type: Array,
      required: true
    },
    valueKey: {
      type: String,
      required: true
    },
    elementId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    yFormatType: {
      type: String,
      default: 'USD'
    },
    tooltip: {
      type: String,
      default: '',
      required: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    pointStyles: [
      'circle',
      'triangle',
      'rect',
      'star',
      'cross',
      'line',
      'crossRot',
      'dash',
      'rectRounded',
      'rectRot'
    ],
    redrawFlag: false,
    hiddenDataSetIndices: [],
    dataSets: []
  }),

  watch: {
    data () {
      this.hiddenDataSetIndices = []
      this.redrawFlag = false
      this.$nextTick(() => {
        this.redrawFlag = true
      })
    },

    yFormatType () {
      this.redrawFlag = false
      this.$nextTick(() => {
        this.redrawFlag = true
      })
    },

    showLegend () {
      this.redrawFlag = false
      this.$nextTick(() => {
        this.redrawFlag = true
      })
    }
  },

  computed: {
    ...mapGetters('ticket', ['contractMode']),

    canvasContainerHeight () {
      const DEFAULT = '98%'
      const returnDefault = this.$vuetify.breakpoint.mdAndUp || !this.showLegend || this.dataSets.length < 6
      if (returnDefault) return DEFAULT
      return `max(98%, calc(98% + ${this.dataSets.length * 16}px))`
    }
  },

  mounted () {
    if (this.data.length > 0) this.redrawFlag = true
  },

  methods: {
    numberWithCommas,
    formatMoney,

    isOneLocation (data) {
      let isOneLocation = true
      const locationName = data[0].locationName
      for (let i = 0; i < data.length; i++) {
        const isSame = data[i].locationName === locationName
        if (!isSame) {
          isOneLocation = false
          break
        }
      }
      return isOneLocation
    },

    getDatasets (isOneLocation) {
      const datasets = []
      let labels = []
      if (this.data.length > 0) {
        labels = this.data[0].dates
      } else {
        return
      }
      const styleMap = {}
      let pointsUsed = 0

      const getLabel = (locationName, deckName) => {
        if (isOneLocation) return deckName

        return `${locationName}-${deckName}`
      }

      this.data.forEach((deck, i) => {
        let color = ''
        let pointStyle = ''
        if (styleMap[deck.locationName] === undefined) {
          styleMap[deck.locationName] = { pointStyle: '', colorsUsed: 0 }
          styleMap[deck.locationName].pointStyle = this.pointStyles[pointsUsed % this.pointStyles.length]
          pointsUsed++
        }
        pointStyle = styleMap[deck.locationName].pointStyle
        color = getChartColor(i, this.data.length, this.contractMode.value)
        styleMap[deck.locationName].colorsUsed++

        const dataSet = {
          data: deck[this.valueKey],
          label: getLabel(deck.locationName, deck.name),
          fill: false,
          borderColor: color,
          pointBackgroundColor: color,
          pointStyle: pointStyle,
          pointRadius: 5,
          cubicInterpolationMode: 'monotone',
          hidden: this.hiddenDataSetIndices.includes(i)
        }
        datasets.push(dataSet)
      })

      return {
        datasets: datasets,
        labels: labels
      }
    },

    createChart (canvas) {
      const isOneLocation = this.isOneLocation(this.data)
      const chartData = this.getDatasets(isOneLocation)
      this.dataSets = chartData.datasets
      const format = this.yFormatType

      const legendClickHandler = (e, legendItem, legend) => { // Reference: chartjs.org/docs/latest/configuration/legend.html#custom-on-click-actions
        const index = legendItem.datasetIndex
        const chart = legend.chart
        if (chart.isDatasetVisible(index)) {
          chart.hide(index)
          legendItem.hidden = true
          this.hiddenDataSetIndices.push(index)
        } else {
          chart.show(index)
          legendItem.hidden = false
          this.hiddenDataSetIndices = this.hiddenDataSetIndices.filter(dsIndex => dsIndex !== index)
        }
      }

      const chartConfig = {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 500,
          plugins: {
            tooltip: {
              callbacks: {
                label: (tooltipItem) => {
                  const value = format === 'USD'
                    ? formatMoney(tooltipItem.parsed.y)
                    : numberWithCommas(tooltipItem.parsed.y, 3)

                  return `${tooltipItem.dataset.label}: ${value}`
                }
              }
            },
            legend: {
              display: this.showLegend,
              onClick: legendClickHandler
            }
          },
          scales: {
            x: {
              grid: {
                display: true
              }
            },
            y: {
              display: true,
              grid: {
                display: true
              },
              ticks: {
                callback: function (val, index) {
                  if (format === 'USD') {
                    return formatMoney(val)
                  } else {
                    return numberWithCommas(val, 3)
                  }
                }
              }
            }
          }
        }
      }
      this.deckWeightChart = new Chart(canvas, chartConfig)
    }
  }
}
</script>
